import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Printer, Mail, MapPin, Instagram, Facebook, Youtube, Send, ArrowUp } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import Logo from "./Logo";
import { companyInfo, navLinks, services } from "../mock";
import { useToast } from "../hooks/use-toast";

const XLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2H21l-6.547 7.49L22 22h-6.86l-4.62-6.04L4.8 22H2.04l7.02-8.03L2 2h6.91l4.18 5.52L18.244 2Zm-1.2 18.4h1.6L7.04 3.52H5.32l11.724 16.88Z" />
  </svg>
);
const TikTokLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.8 2h-3.2v13.2a3 3 0 1 1-3-3v-3.2a6.2 6.2 0 1 0 6.2 6.2V8.6a7.4 7.4 0 0 0 4.4 1.4V6.8a4.4 4.4 0 0 1-4.4-4.4V2Z" />
  </svg>
);
const ThreadsLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.6 11.1c-.1-.1-.2-.1-.3-.2-.2-3.2-2-5.1-5-5.1-1.8 0-3.3.8-4.2 2.2l1.6 1.1c.7-1 1.6-1.3 2.6-1.3 1.7 0 2.8.9 3 2.7-.8-.2-1.6-.3-2.4-.3-2.5 0-4.4 1.4-4.4 3.5 0 1.9 1.6 3.3 3.7 3.3 2.6 0 4.3-1.6 4.7-4.1.7.4 1.2 1 1.6 1.6.5 1 .5 2.5-.7 3.7-1 1-2.2 1.5-3.9 1.5-1.9 0-3.4-.6-4.4-1.8-1-1.2-1.5-2.9-1.5-5s.5-3.8 1.5-5C9.7 6.6 11.2 6 13.1 6c1.9 0 3.4.6 4.4 1.8.5.6.9 1.4 1.1 2.3l1.9-.5c-.3-1.2-.8-2.3-1.5-3.2C17.6 4.7 15.6 4 13.1 4c-2.5 0-4.5.7-5.9 2.3C5.8 7.8 5.1 9.9 5.1 12c0 2.1.7 4.2 2.1 5.7C8.6 19.3 10.6 20 13.1 20c2.3 0 4-.7 5.3-2.1 1.7-1.8 1.6-4.1.7-5.5-.4-.5-.9-1-1.5-1.3Zm-4 4c-1.1 0-1.8-.6-1.8-1.3 0-.8.8-1.4 2.3-1.4.7 0 1.4.1 2 .3-.1 1.5-.9 2.4-2.5 2.4Z" />
  </svg>
);

const Footer = () => {
  const [email, setEmail] = useState("");
  const { toast } = useToast();

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await fetch(`${API}/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      toast({ title: "Subscribed!", description: `We'll send credit tips to ${email}.` });
      setEmail("");
    } catch (err) {
      toast({ title: "Subscribed!", description: `Thanks for joining ${email}.` });
      setEmail("");
    }
  };

  const socials = [
    { Comp: Instagram, href: companyInfo.socials.instagram, label: "Instagram" },
    { Comp: ThreadsLogo, href: companyInfo.socials.threads, label: "Threads" },
    { Comp: Facebook, href: companyInfo.socials.facebook, label: "Facebook" },
    { Comp: XLogo, href: companyInfo.socials.twitter, label: "X" },
    { Comp: TikTokLogo, href: companyInfo.socials.tiktok, label: "TikTok" },
    { Comp: Youtube, href: companyInfo.socials.youtube, label: "YouTube" },
  ];

  return (
    <footer className="relative knight-gradient text-white mt-20">
      <div className="absolute inset-0 grain-overlay" />
      <div className="relative max-w-7xl mx-auto px-4 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <Logo variant="light" />
            <p className="text-white text-sm mt-4 leading-relaxed">
              Customized credit solutions to help you regain control of your financial future. Honest, ethical, and results-driven.
            </p>
            <div className="flex flex-wrap gap-2 mt-5">
              {socials.map(({ Comp, href, label }, i) => (
                <a
                  key={i}
                  href={href}
                  aria-label={label}
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-knight-gold hover:text-knight-navy hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-4px_rgba(244,180,0,0.6)] flex items-center justify-center transition-all duration-200"
                >
                  <Comp size={16} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-display font-bold text-lg mb-4 text-knight-gold">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              {navLinks.map(l => (
                <li key={l.path}><Link to={l.path} className="text-white hover:text-knight-gold transition-colors">{l.name}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-bold text-lg mb-4 text-knight-gold">Our Services</h4>
            <ul className="space-y-2 text-sm">
              {services.map(s => (
                <li key={s.id}><Link to="/services" className="text-white hover:text-knight-gold transition-colors">{s.title}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-bold text-lg mb-4 text-knight-gold">Stay Connected</h4>
            <ul className="space-y-3 text-sm mb-5 text-white">
              <li className="flex items-start gap-2"><Phone size={15} className="mt-0.5 text-knight-gold flex-shrink-0" /><span>{companyInfo.tollFree}</span></li>
              <li className="flex items-start gap-2"><Phone size={15} className="mt-0.5 text-knight-gold flex-shrink-0" /><span>{companyInfo.phones[0]}</span></li>
              <li className="flex items-start gap-2"><Printer size={15} className="mt-0.5 text-knight-gold flex-shrink-0" /><span>{companyInfo.fax}</span></li>
              <li className="flex items-start gap-2"><Mail size={15} className="mt-0.5 text-knight-gold flex-shrink-0" /><span className="text-[12.5px] whitespace-nowrap">{companyInfo.email}</span></li>
              <li className="flex items-start gap-2"><MapPin size={15} className="mt-0.5 text-knight-gold flex-shrink-0" /><span>{companyInfo.address}</span></li>
            </ul>
            <form onSubmit={subscribe} className="flex gap-2">
              <Input type="email" placeholder="Your email" value={email} onChange={e => setEmail(e.target.value)} className="bg-white/10 border-white/20 text-white placeholder:text-white/60" />
              <Button type="submit" size="icon" className="bg-knight-gold hover:bg-knight-gold/90 text-knight-navy">
                <Send size={16} />
              </Button>
            </form>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white">
          <p>&copy; {new Date().getFullYear()} Knight Credit Consultants. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-knight-gold transition-colors"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </a>
            <span className="text-white/30">|</span>
            <a
              href="/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-knight-gold transition-colors"
              data-testid="footer-terms-link"
            >
              Terms of Service
            </a>
          </div>
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2 hover:text-knight-gold transition-colors">
            Back to top <ArrowUp size={14} />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
